import type { TimesheetStatus, TimesheetEntry, DayKey } from '@/types/timesheet';

const DAY_KEYS: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Determines whether entries contain daily notes data.
 * Returns true if any entry has at least one non-empty daily note.
 */
function entriesHaveDailyNotes(entries: TimesheetEntry[]): boolean {
  return entries.some(entry =>
    entry.dailyNotes &&
    DAY_KEYS.some(day => entry.dailyNotes?.[day]?.trim())
  );
}

/**
 * Statuses that are editable by the employee and should follow the current
 * organisation setting for description type.
 */
const EDITABLE_STATUSES: Set<TimesheetStatus> = new Set(['draft', 'rejected', 'recalled']);

/**
 * Returns the effective description type for a given timesheet.
 *
 * - Editable timesheets (draft, rejected, recalled) use the current org setting
 *   so the employee works with the latest configuration.
 * - Non-editable timesheets (submitted, approved, locked) infer the mode from
 *   the actual entry data so that changing the org setting does not retroactively
 *   alter how historical timesheets are displayed.
 */
export function getEffectiveDescriptionType(
  orgDescriptionType: 'row' | 'daily',
  timesheetStatus: TimesheetStatus,
  entries: TimesheetEntry[],
): 'row' | 'daily' {
  if (EDITABLE_STATUSES.has(timesheetStatus)) {
    return orgDescriptionType;
  }

  // Non-editable: infer from data
  return entriesHaveDailyNotes(entries) ? 'daily' : 'row';
}
